import { lazy } from "react";
import { Navigate, useParams } from "react-router-dom";

//Dashboard - Lazy loaded for code splitting
// All three dashboards migrated to modern stack — Wave 3 (features/dashboards/).
// Legacy files at src/pages/Dashboard/* kept under LEGACY headers for the 2-sprint
// cooling period. See features/dashboards/README.md.
const DashboardEcommerce = lazy(() => import("../features/dashboards/pages/dashboard-ecommerce-page"));
const DashboardModern = lazy(() => import("../features/dashboards/pages/dashboard-modern-page"));
// AdminModernDashboard kept as static import (the original comment about chunk loading still applies)
import AdminModernDashboard from "../features/dashboards/pages/admin-modern-dashboard-page";

// Lead Management - Lazy loaded
// Leads migrated to modern stack — Wave 3 (features/leads/ + features/lead-attachments/).
// Legacy files at src/pages/Leads/Lead/* kept under LEGACY headers for cooling period.
// See features/leads/README.md.
const LeadList = lazy(() => import("../features/leads/pages/leads-list-page"));
const LeadCreate = lazy(() => import("../features/leads/pages/leads-create-page"));
const LeadEdit = lazy(() => import("../features/leads/pages/leads-edit-page"));
const LeadView = lazy(() => import("../features/leads/pages/leads-view-page"));

// Client Management - Lazy loaded
// Clients migrated to modern stack — Wave 4 (features/clients/).
const ClientList = lazy(() => import("../features/clients/pages/clients-list-page"));
const ClientCreate = lazy(() => import("../features/clients/pages/clients-create-page"));
const ClientEdit = lazy(() => import("../features/clients/pages/clients-edit-page"));
const ClientView = lazy(() => import("../features/clients/pages/clients-view-page"));
// Client Meetings migrated to modern stack — Wave 4 (features/client-meetings/).
// Includes the long-deferred FullCalendar wrap with Tailwind theme overrides.
const MeetingSchedule = lazy(() => import("../features/client-meetings/pages/client-meetings-list-page"));
const MeetingCreate = lazy(() => import("../features/client-meetings/pages/client-meetings-create-page"));
const MeetingEdit = lazy(() => import("../features/client-meetings/pages/client-meetings-edit-page"));
const MeetingView = lazy(() => import("../features/client-meetings/pages/client-meetings-view-page"));
const MeetingCalendar = lazy(() => import("../features/client-meetings/pages/client-meetings-calendar-page"));
const MeetingReschedule = lazy(() => import("../features/client-meetings/pages/client-meetings-reschedule-page"));

// Work Order Management - Lazy loaded
// Work Orders migrated to modern stack — Wave 6 (features/work-orders/).
// Legacy files at src/pages/Clients/WorkOrder/* kept under cooling period.
const WorkOrderList = lazy(() => import("../features/work-orders/pages/work-orders-list-page"));
const WorkOrderCreate = lazy(() => import("../features/work-orders/pages/work-orders-create-page"));
const WorkOrderEdit = lazy(() => import("../features/work-orders/pages/work-orders-edit-page"));
const WorkOrderView = lazy(() => import("../features/work-orders/pages/work-orders-view-page"));

// Job Management - Lazy loaded (formerly Project)
// Jobs migrated to modern stack — features/jobs/. Legacy pages/Clients/Job/*
// retained only for the /jobs/* redirect shims (JobRedirect/JobViewRedirect).
const ProjectList = lazy(() => import("../features/jobs/pages/jobs-list-page"));
const ProjectCreate = lazy(() => import("../features/jobs/pages/jobs-create-page"));
const ProjectEdit = lazy(() => import("../features/jobs/pages/jobs-edit-page"));
const ProjectView = lazy(() => import("../features/jobs/pages/jobs-view-page"));

// Site Management - Lazy loaded
// Client Sites migrated to modern stack — Wave 4 (features/client-sites/).
// Includes Leaflet map view with SSR-safe defer and graceful fallback.
const SitesList = lazy(() => import("../features/client-sites/pages/client-sites-list-page"));
const SiteCreate = lazy(() => import("../features/client-sites/pages/client-sites-create-page"));
const SiteEdit = lazy(() => import("../features/client-sites/pages/client-sites-edit-page"));
const SiteView = lazy(() => import("../features/client-sites/pages/client-sites-view-page"));

// Contact Management - Lazy loaded
// Client Contacts migrated to modern stack — Wave 4 (features/client-contacts/).
const ContactsList = lazy(() => import("../features/client-contacts/pages/client-contacts-list-page"));
const ContactCreate = lazy(() => import("../features/client-contacts/pages/client-contacts-create-page"));
const ContactEdit = lazy(() => import("../features/client-contacts/pages/client-contacts-edit-page"));
const ContactView = lazy(() => import("../features/client-contacts/pages/client-contacts-view-page"));

// Account Management - Lazy loaded
// Departments migrated to modern stack — Wave 2. Legacy files at
// src/pages/Account/Department/* kept under LEGACY headers for the 2-sprint
// cooling period. See features/departments/README.md.
const DepartmentList = lazy(() => import("../features/departments/pages/departments-list-page"));
const DepartmentCreate = lazy(() => import("../features/departments/pages/departments-create-page"));
const DepartmentEdit = lazy(() => import("../features/departments/pages/departments-edit-page"));
// TenantRoles migrated to modern stack — Wave 2. Legacy files at
// src/pages/Account/TenantRole/* kept under LEGACY headers. See features/tenant-roles/README.md.
const TenantRoleList = lazy(() => import("../features/tenant-roles/pages/tenant-roles-list-page"));
const TenantRoleCreate = lazy(() => import("../features/tenant-roles/pages/tenant-roles-create-page"));
const TenantRoleEdit = lazy(() => import("../features/tenant-roles/pages/tenant-roles-edit-page"));
const TenantRoleView = lazy(() => import("../features/tenant-roles/pages/tenant-roles-view-page"));
// NOTE: ProfileList removed - Super Admin profiles page will never be implemented
// GlobalUsers migrated to modern stack — Wave 2. Legacy files at
// src/pages/Account/GlobalUser/* kept under LEGACY headers. See features/global-users/README.md.
const GlobalUserList = lazy(() => import("../features/global-users/pages/global-users-list-page"));
const GlobalUserCreate = lazy(() => import("../features/global-users/pages/global-users-create-page"));
const GlobalUserEdit = lazy(() => import("../features/global-users/pages/global-users-edit-page"));
const GlobalUserView = lazy(() => import("../features/global-users/pages/global-users-view-page"));

// Settings - Lazy loaded
// SystemConfig migrated to modern stack (features/system-config/) — Wave 1 Gold Standard.
// The legacy file at pages/Settings/General/SystemConfig.tsx is kept under a LEGACY
// header for the 2-sprint cooling period before deletion. See MIGRATION_PLAYBOOK.md
// and features/system-config/README.md.
const SystemConfig = lazy(() => import("../features/system-config/pages/system-config-page"));
// BasicInfo migrated to modern stack — Wave 2. Legacy at src/pages/Settings/General/BasicInfo.tsx
// under LEGACY header. See features/basic-info/README.md.
const BasicInfo = lazy(() => import("../features/basic-info/pages/basic-info-page"));
// RentalConfig migrated — Wave 2. Legacy at src/pages/Settings/Rental/TenantRentalConfig.tsx
// under LEGACY header. See features/rental-config/README.md.
const TenantRentalConfig = lazy(() => import("../features/rental-config/pages/rental-config-page"));
// HRMS — Settings sub-section (Phase 0/1: dashboard, employees, holidays, leave).
const HrmsDashboard = lazy(() => import("../features/hrms/pages/hrms-dashboard-page"));
const HrmsHolidays = lazy(() => import("../features/hrms/pages/hrms-holidays-page"));
const HrmsLeave = lazy(() => import("../features/hrms/pages/hrms-leave-page"));
const HrmsCompetency = lazy(() => import("../features/hrms/pages/hrms-competency-page"));
const HrmsTraining = lazy(() => import("../features/hrms/pages/hrms-training-page"));
const HrmsAttendance = lazy(() => import("../features/hrms/pages/hrms-attendance-page"));
const HrmsGlobalCalendar = lazy(() => import("../features/hrms/pages/hrms-global-calendar-page"));
const HrmsPerformance = lazy(() => import("../features/hrms/pages/hrms-performance-page"));
const HrmsDocuments = lazy(() => import("../features/hrms/pages/hrms-documents-page"));

// Tenant Locations - Lazy loaded
// Migrated to modern stack — Wave 2. Legacy files at src/pages/TenantLocations/*
// under LEGACY headers. See features/tenant-locations/README.md.
const TenantLocationList = lazy(() => import("../features/tenant-locations/pages/tenant-locations-list-page"));
const TenantLocationCreate = lazy(() => import("../features/tenant-locations/pages/tenant-locations-create-page"));
const TenantLocationEdit = lazy(() => import("../features/tenant-locations/pages/tenant-locations-edit-page"));
const TenantLocationView = lazy(() => import("../features/tenant-locations/pages/tenant-locations-view-page"));

// Site Types - Lazy loaded
// Migrated to modern stack — Wave 2. Legacy files at src/pages/SiteTypes/* under
// LEGACY headers. See features/site-types/README.md.
const SiteTypeList = lazy(() => import("../features/site-types/pages/site-types-list-page"));
const SiteTypeCreate = lazy(() => import("../features/site-types/pages/site-types-create-page"));
const SiteTypeEdit = lazy(() => import("../features/site-types/pages/site-types-edit-page"));

// Client Rental Config - Lazy loaded
// Client Rental Config migrated to modern stack — Wave 4 (features/client-rental-config/).
// Hierarchical override over tenant rental config; null fields inherit tenant default.
const ClientRentalConfig = lazy(() => import("../features/client-rental-config/pages/client-rental-config-page"));

// Products - Lazy loaded
// Products migrated to modern stack — Wave 3 (features/products/).
const ProductList = lazy(() => import("../features/products/pages/products-list-page"));
const ProductCreate = lazy(() => import("../features/products/pages/products-create-page"));
const ProductEdit = lazy(() => import("../features/products/pages/products-edit-page"));
const ProductView = lazy(() => import("../features/products/pages/products-view-page"));

// Stock Items - Lazy loaded
// Stock Items migrated to modern stack — Wave 3 (features/stock-items/).
const StockItemList = lazy(() => import("../features/stock-items/pages/stock-items-list-page"));
const StockItemCreate = lazy(() => import("../features/stock-items/pages/stock-items-create-page"));
const StockItemEdit = lazy(() => import("../features/stock-items/pages/stock-items-edit-page"));
const StockItemView = lazy(() => import("../features/stock-items/pages/stock-items-view-page"));
const StockMovementHistory = lazy(() => import("../features/stock-items/pages/stock-items-history-page"));
const InventoryDashboard = lazy(() => import("../pages/StockItems/InventoryDashboard"));

// Purchase Orders - Lazy loaded
// Purchase Orders migrated to modern stack — Wave 3 (features/purchase-orders/).
const PurchaseOrderList = lazy(() => import("../features/purchase-orders/pages/purchase-orders-list-page"));
const PurchaseOrderCreate = lazy(() => import("../features/purchase-orders/pages/purchase-orders-create-page"));
const PurchaseOrderEdit = lazy(() => import("../features/purchase-orders/pages/purchase-orders-edit-page"));
const PurchaseOrderView = lazy(() => import("../features/purchase-orders/pages/purchase-orders-view-page"));

// Suppliers - Lazy loaded
// Suppliers migrated to modern stack — Wave 3 (features/suppliers/).
const SupplierList = lazy(() => import("../features/suppliers/pages/suppliers-list-page"));
const SupplierCreate = lazy(() => import("../features/suppliers/pages/suppliers-create-page"));
const SupplierEdit = lazy(() => import("../features/suppliers/pages/suppliers-edit-page"));
const SupplierView = lazy(() => import("../features/suppliers/pages/suppliers-view-page"));

// Assets - Lazy loaded
// Assets migrated to modern stack — Wave 3 (features/assets/).
const AssetList = lazy(() => import("../features/assets/pages/assets-list-page"));
const AssetCreate = lazy(() => import("../features/assets/pages/assets-create-page"));
const AssetEdit = lazy(() => import("../features/assets/pages/assets-edit-page"));
const AssetView = lazy(() => import("../features/assets/pages/assets-view-page"));

// Tenant Labour Rates - Lazy loaded
// Migrated to modern stack — Wave 2. Legacy files at src/pages/TenantLabourRates/*
// under LEGACY headers. See features/tenant-labour-rates/README.md.
const TenantLabourRateList = lazy(() => import("../features/tenant-labour-rates/pages/tenant-labour-rates-list-page"));
const TenantLabourRateCreate = lazy(() => import("../features/tenant-labour-rates/pages/tenant-labour-rates-create-page"));
const TenantLabourRateEdit = lazy(() => import("../features/tenant-labour-rates/pages/tenant-labour-rates-edit-page"));

// Tenant Cartage Rates - Lazy loaded (features/tenant-cartage-rates/).
// Per-kilometre rate by CartageVehicleType. Mirrors the labour-rates module.
const TenantCartageRateList = lazy(() => import("../features/tenant-cartage-rates/pages/tenant-cartage-rates-list-page"));
const TenantCartageRateCreate = lazy(() => import("../features/tenant-cartage-rates/pages/tenant-cartage-rates-create-page"));
const TenantCartageRateEdit = lazy(() => import("../features/tenant-cartage-rates/pages/tenant-cartage-rates-edit-page"));

// Client Labour Rates + Client Cartage Rates — per-client rate overrides.
// Relocated 2026-06: these are no longer standalone Settings screens. They now
// live as the "Rate Overrides" tab on the Client view (clients-view-page →
// ClientRateOverridesPanel), which embeds the rate forms with the client
// preselected. The standalone routes were removed; the reusable feature
// modules (forms / hooks / schemas) remain and are imported by that tab.

// Quotes - Lazy loaded
//
// ✅ ROUTED TO MODERN (Tailwind/shadcn/RHF/TanStack Query) — 2026-05-21
// All 8 phases of the rewrite (REWRITE_PLAN.md) are complete:
//   Phase 1 — Schema + lib utilities
//   Phase 2 — Add Item Dialog with all 4 sub-form strategies
//   Phase 3 — Job Card UI with rental + compliance + dropdown guards
//   Phase 4 — Form Header with auto-gen quote # + Job picker cascade
//   Phase 5 — View page with expandable jobs + nested breakdowns + totals
//   Phase 6 — Status workflow actions + remarks confirmation dialog
//   Phase 7 — Preview To/Attn + Volume Conflict dialog + List Card view
//   Phase 8 — Route flip + form integration
//
// Legacy pages at `src/pages/Quotes/*` are retained for one cooling sprint
// as rollback safety. Will be deleted in the next quarterly cleanup window.
const CreateQuoteContainer = lazy(() => import("../features/quotes/pages/quotes-create-page"));
const EditQuoteContainer = lazy(() => import("../features/quotes/pages/quotes-edit-page"));
const QuotePreview = lazy(() => import("../features/quotes/pages/quotes-preview-page"));
const QuoteList = lazy(() => import("../features/quotes/pages/quotes-list-page"));
const QuoteView = lazy(() => import("../features/quotes/pages/quotes-view-page"));
const QuotesKanban = lazy(() => import("../features/quotes/pages/quotes-kanban-page"));
const QuotesReport = lazy(() => import("../features/quotes/pages/quotes-report-page"));

// Module Homes - Lazy loaded
// Module Homes migrated to modern stack — Wave 3 (features/module-homes/).
// Tiles are role-filtered via @/shared/auth (Settings/Finance gated to admins).
const ClientsModuleHome = lazy(() => import("../features/module-homes/pages/clients-module-home"));
const LeadsModuleHome = lazy(() => import("../features/module-homes/pages/leads-module-home"));
const QuotesModuleHome = lazy(() => import("../features/module-homes/pages/quotes-module-home"));
const InventoryModuleHome = lazy(() => import("../features/module-homes/pages/inventory-module-home"));
const SettingsModuleHome = lazy(() => import("../features/module-homes/pages/settings-module-home"));
const ScaffoldModuleHome = lazy(() => import("../features/module-homes/pages/scaffold-module-home"));
const JobsModuleHome = lazy(() => import("../features/module-homes/pages/jobs-module-home"));
const FinanceModuleHome = lazy(() => import("../features/module-homes/pages/finance-module-home"));

// Compliance Geo-Zones — new module (features/compliance-zones/). Admin draws
// polygon compliance regions, sees Client Sites inside (point-in-polygon),
// manages inspection groups, and reports jobs by zone. Mock-first.
const ComplianceModuleHome = lazy(() => import("../features/compliance-zones/pages/compliance-module-home"));
const ComplianceZonesPage = lazy(() => import("../features/compliance-zones/pages/compliance-zones-page"));
const ComplianceGroupsPage = lazy(() => import("../features/compliance-zones/pages/compliance-groups-page"));
const ComplianceReportPage = lazy(() => import("../features/compliance-zones/pages/compliance-report-page"));
// Compliance Checks — scheduled compliance checks (features/compliance-checks/).
const ComplianceInspectionsPage = lazy(() => import("../features/compliance-checks/pages/compliance-inspections-page"));
const ComplianceFixTasksPage = lazy(() => import("../features/compliance-fix-tasks/pages/compliance-fix-tasks-page"));
const ComplianceDispatchPage = lazy(() => import("../features/compliance-dispatch/pages/compliance-dispatch-page"));

// Scaffolds - Lazy loaded
// Scaffolds migrated to modern stack — Wave 6 (features/scaffolds/).
// Includes Scheduler with FullCalendar wrap + status lifecycle timeline.
// Legacy files at src/pages/Scaffolds/* kept under cooling period.
const ScaffoldList = lazy(() => import("../features/scaffolds/pages/scaffolds-list-page"));
const ScaffoldDetail = lazy(() => import("../features/scaffolds/pages/scaffolds-detail-page"));
const Scheduler = lazy(() => import("../features/scaffolds/pages/scaffolds-scheduler-page"));

// Invoicing - Lazy loaded
// Migrated to modern stack — Wave 7 (features/invoicing/).
// Greenfield: no legacy invoicing pages existed. See features/invoicing/README.md.
const InvoicingList = lazy(() => import("../features/invoicing/pages/invoicing-list-page"));
const InvoicingCreate = lazy(() => import("../features/invoicing/pages/invoicing-create-page"));
const InvoicingEdit = lazy(() => import("../features/invoicing/pages/invoicing-edit-page"));
const InvoicingView = lazy(() => import("../features/invoicing/pages/invoicing-view-page"));
const InvoicingReview = lazy(() => import("../features/invoicing/pages/invoicing-review-page"));
const InvoicingPreview = lazy(() => import("../features/invoicing/pages/invoicing-preview-page"));

// Rentals - Lazy loaded
// Migrated to modern stack — Wave 7 (features/rentals/).
// Greenfield: no legacy rental pages existed. See features/rentals/README.md.
const RentalsList = lazy(() => import("../features/rentals/pages/rentals-list-page"));
const RentalsDetail = lazy(() => import("../features/rentals/pages/rentals-detail-page"));

// Planner module retired 2026-06-19 (features/planner + pages/Planner deleted).
// The Dispatch Board is the surviving daily-ops tool — now top-level at /dispatch-board.

// Dispatch Board — Wave 8 (features/dispatch-board/) — new crew-centric Kanban
// for daily dispatch. Isolated module: no modifications to existing planner
// code. Mock data in S1; real API in S2.
const DispatchBoardPage = lazy(() => import("../features/dispatch-board/pages/dispatch-board-page"));

// Field App — Dashboard + Timesheet + Activity
// Migrated to modern stack — Wave 7 (features/field-app/).
// Legacy files at src/pages/FieldApp/* and src/Layouts/FieldAppShell/* kept under
// LEGACY headers for the 2-sprint cooling period. See features/field-app/README.md.
const FieldDashboard = lazy(() => import("../features/field-app/pages/field-dashboard-page"));
const WorkerDayView = lazy(() => import("../features/field-app/pages/field-day-view-page"));
const ActivityDetail = lazy(() => import("../features/field-app/pages/field-activity-detail-page"));
const FieldCrew = lazy(() => import("../features/field-app/pages/field-crew-page"));
const FieldCapture = lazy(() => import("../features/field-app/pages/field-capture-page"));
const FieldVisits = lazy(() => import("../features/field-app/pages/field-visits-page"));
const FieldVisitDetail = lazy(() => import("../features/field-app/pages/field-visit-detail-page"));
const FieldChecklists = lazy(() => import("../features/field-app/pages/field-checklists-page"));
const FieldTasks = lazy(() => import("../features/field-app/pages/field-tasks-page"));
const FieldAnalytics = lazy(() => import("../features/field-app/pages/field-analytics-page"));
const FieldProfile = lazy(() => import("../features/field-app/pages/field-profile-page"));
const FieldScaffold = lazy(() => import("../features/field-app/pages/field-scaffold-page"));
const FieldScaffolds = lazy(() => import("../features/field-app/pages/field-scaffolds-page"));

// Timesheet Prototype — Admin
// Timesheet admin migrated to modern stack — Wave 6 (features/timesheet-admin/).
// /timesheets is the day-view (the legacy TimesheetList was a thin list; modern uses day-view as default).
// Legacy files at src/pages/Timesheet/* kept under cooling period.
const TimesheetList = lazy(() => import("../features/timesheet-admin/pages/timesheets-day-view-page"));
const TimesheetDayView = lazy(() => import("../features/timesheet-admin/pages/timesheets-day-view-page"));
const TimesheetCreateOnBehalf = lazy(() => import("../features/timesheet-admin/pages/timesheets-create-on-behalf-page"));
const TimesheetBulkFill = lazy(() => import("../features/timesheet-admin/pages/timesheets-bulk-fill-page"));
const TimesheetDailyReview = lazy(() => import("../features/timesheet-admin/pages/timesheets-daily-review-page"));
const TimesheetRateMatrix = lazy(() => import("../features/timesheet-admin/pages/timesheets-rate-matrix-page"));
const TimesheetPrepChargeMatrix = lazy(() => import("../features/timesheet-admin/pages/timesheets-prep-charge-matrix-page"));

// Error Pages - Lazy loaded
const Basic404 = lazy(() => import("../pages/AuthenticationInner/Errors/Basic404"));
const Cover404 = lazy(() => import("../pages/AuthenticationInner/Errors/Cover404"));
const Alt404 = lazy(() => import("../pages/AuthenticationInner/Errors/Alt404"));
const Error500 = lazy(() => import("../pages/AuthenticationInner/Errors/Error500"));
const Offlinepage = lazy(() => import("../pages/AuthenticationInner/Errors/Offlinepage"));

// Authentication - Lazy loaded
// Auth migrated to modern stack — Wave 2.5. Legacy files at
// src/pages/Authentication/components/* kept under LEGACY headers for the
// 2-sprint cooling period. See features/auth/README.md and shared/auth/README.md.
const Login = lazy(() => import("../features/auth/pages/login-page"));
const OtpVerification = lazy(() => import("../features/auth/pages/otp-verification-page"));
const ForgetPasswordPage = lazy(() => import("../features/auth/pages/forgot-password-page"));
// Logout stays on legacy — it has no UI, just runs the logout thunk and redirects.
const Logout = lazy(() => import("../pages/Authentication/components/Logout"));
// Modern profile page — new route, no legacy equivalent.
const ProfilePage = lazy(() => import("../features/profile/pages/profile-page"));

// Field App Auth (mobile-optimized) — migrated to modern stack (Wave 7)
const FieldLogin = lazy(() => import("../features/field-app/pages/field-login-page"));
const FieldOtpVerification = lazy(() => import("../features/field-app/pages/field-otp-verification-page"));

/**
 * QR deep-link → scaffold detail. The physical QR sticker encodes
 * `/qr/scaffold/:id`; resolve it to the existing (auth-guarded) scaffold detail
 * page. Unauthenticated scans are sent to login and returned here afterwards
 * (see AuthProtected + helpers/authReturnTo).
 */
function QrScaffoldRedirect() {
  const { id } = useParams<{ id: string }>();
  return <Navigate to={`/scaffolds/view/${id ?? ""}`} replace />;
}

const authProtectedRoutes = [
  { path: "/dashboard", component: DashboardEcommerce },
  { path: "/index", component: DashboardEcommerce },
  { path: "/dashboard-modern", component: DashboardModern }, // New Route
  { path: "/admin-dashboard", component: AdminModernDashboard }, // Scaffold Operations Command Center

  // Lead Management
  { path: "/leads", component: LeadsModuleHome },
  { path: "/leads/list", component: LeadList },
  { path: "/leads/create", component: LeadCreate },
  { path: "/leads/edit/:id", component: LeadEdit },
  { path: "/leads/view/:id", component: LeadView },

  // Client Management
  { path: "/clients", component: ClientsModuleHome },
  { path: "/clients/list", component: ClientList },
  { path: "/clients/create", component: ClientCreate },
  { path: "/clients/view/:id", component: ClientView },
  { path: "/clients/edit/:id", component: ClientEdit },
  { path: "/clients/meetings", component: MeetingSchedule },

  // Scaffold
  { path: "/scaffold", component: ScaffoldModuleHome },
  { path: "/scaffolds/list", component: ScaffoldList },
  { path: "/scaffolds/view/:id", component: ScaffoldDetail },
  // QR sticker deep-link → scaffold detail (resolves /qr/scaffold/:id).
  { path: "/qr/scaffold/:id", component: QrScaffoldRedirect },

  // Scheduler (top-level route)
  { path: "/scheduler", component: Scheduler },

  // Invoicing — Wave 7 (greenfield)
  { path: "/invoices", component: () => <Navigate to="/invoices/list" /> },
  { path: "/invoices/list", component: InvoicingList },
  { path: "/invoices/create", component: InvoicingCreate },
  { path: "/invoices/edit/:id", component: InvoicingEdit },
  { path: "/invoices/view/:id", component: InvoicingView },
  { path: "/invoices/review/:id", component: InvoicingReview },
  { path: "/invoices/preview/:id", component: InvoicingPreview },

  // Rentals — Wave 7 (greenfield)
  { path: "/rentals", component: () => <Navigate to="/rentals/list" /> },
  { path: "/rentals/list", component: RentalsList },
  { path: "/rentals/detail/:id", component: RentalsDetail },

  // "Planner" nav opens the Dispatch Board directly (old Planner sub-pages retired
  // 2026-06-19). /planner/dispatch-board kept as an alias so existing deployed
  // links + bookmarks don't break.
  { path: "/planner", component: DispatchBoardPage },
  { path: "/planner/dispatch-board", component: DispatchBoardPage },

  // Timesheet Prototype — Admin
  { path: "/timesheets", component: TimesheetList },
  { path: "/timesheets/day/:userId/:date", component: TimesheetDayView },
  { path: "/timesheets/create", component: TimesheetCreateOnBehalf },
  { path: "/timesheets/bulk/:activityId/:date", component: TimesheetBulkFill },
  { path: "/timesheets/daily-review/:date", component: TimesheetDailyReview },
  { path: "/timesheets/daily-review", component: TimesheetDailyReview },
  { path: "/timesheets/rates", component: TimesheetRateMatrix },
  { path: "/timesheets/prep-charges", component: TimesheetPrepChargeMatrix },

  // Finance
  { path: "/finance", component: FinanceModuleHome },

  // Compliance Geo-Zones
  { path: "/compliance", component: ComplianceModuleHome },
  { path: "/compliance/zones", component: ComplianceZonesPage },
  { path: "/compliance/groups", component: ComplianceGroupsPage },
  { path: "/compliance/report", component: ComplianceReportPage },
  { path: "/compliance/inspections", component: ComplianceInspectionsPage },
  { path: "/compliance/fix-tasks", component: ComplianceFixTasksPage },
  { path: "/compliance/dispatch", component: ComplianceDispatchPage },

  // Work Order Management
  { path: "/clients/work-orders", component: WorkOrderList },
  { path: "/clients/work-orders/create", component: WorkOrderCreate },
  { path: "/clients/work-orders/edit/:id", component: WorkOrderEdit },
  { path: "/clients/work-orders/view/:id", component: WorkOrderView },

  // Job Management (formerly Projects)
  // Job module home — landing tiles (Jobs / Work Orders / Scaffolds). The "Job"
  // top-nav lands here; the Jobs LIST is the /clients/jobs tile below.
  { path: "/job", component: JobsModuleHome },
  // Redirect routes for backward compatibility (components navigate to /jobs/*)
  { path: "/jobs", component: () => <Navigate to="/clients/jobs" /> },
  { path: "/jobs/create", component: () => <Navigate to="/clients/jobs/create" /> },
  { path: "/jobs/edit/:id", component: lazy(() => import("../pages/Clients/Job/JobRedirect")) },
  { path: "/jobs/view/:id", component: lazy(() => import("../pages/Clients/Job/JobViewRedirect")) },
  // Primary routes
  { path: "/clients/jobs", component: ProjectList },
  { path: "/clients/jobs/create", component: ProjectCreate },
  { path: "/clients/jobs/edit/:id", component: ProjectEdit },
  { path: "/clients/jobs/view/:id", component: ProjectView },

  // Meeting Management — canonical paths under `/clients/meetings/*`,
  // matching the locked Sites / Contacts pattern. The shorter `/meetings/*`
  // aliases are kept for legacy in-bound links (dashboards, breadcrumbs).
  { path: "/clients/meetings/create", component: MeetingCreate },
  { path: "/clients/meetings/edit/:id", component: MeetingEdit },
  { path: "/clients/meetings/view/:id", component: MeetingView },
  { path: "/clients/meetings/reschedule/:id", component: MeetingReschedule },
  { path: "/clients/meetings/calendar", component: MeetingCalendar },
  // Legacy aliases — keep until all dashboards have migrated.
  { path: "/meetings", component: MeetingSchedule },
  { path: "/meetings/create", component: MeetingCreate },
  { path: "/meetings/edit/:id", component: MeetingEdit },
  { path: "/meetings/view/:id", component: MeetingView },
  { path: "/meetings/reschedule/:id", component: MeetingReschedule },
  { path: "/meetings/calendar", component: MeetingCalendar },

  // Site Management
  { path: "/clients/sites", component: SitesList },
  { path: "/clients/sites/create", component: SiteCreate },
  { path: "/clients/sites/edit/:id", component: SiteEdit },
  { path: "/clients/sites/view/:id", component: SiteView },

  // My profile (modern stack — Wave 2.5)
  { path: "/profile", component: ProfilePage },

  // Contact Management
  { path: "/clients/contacts", component: ContactsList },
  { path: "/clients/contacts/create", component: ContactCreate },
  { path: "/clients/contacts/edit/:id", component: ContactEdit },
  { path: "/clients/contacts/view/:id", component: ContactView },

  // Account Management
  { path: "/account/departments", component: DepartmentList },
  { path: "/account/departments/create", component: DepartmentCreate },
  { path: "/account/departments/edit/:id", component: DepartmentEdit },
  { path: "/account/tenant-roles", component: TenantRoleList },
  {
    path: "/account/tenant-roles/create",
    component: TenantRoleCreate,
  },
  {
    path: "/account/tenant-roles/edit/:id",
    component: TenantRoleEdit,
  },
  {
    path: "/account/tenant-roles/view/:id",
    component: TenantRoleView,
  },
  // NOTE: /account/profiles route removed - Super Admin profiles page will never be implemented
  { path: "/account/global-users", component: GlobalUserList },
  { path: "/account/global-users/create", component: GlobalUserCreate },
  { path: "/account/global-users/edit/:id", component: GlobalUserEdit },
  { path: "/account/global-users/view/:id", component: GlobalUserView },

  // Settings
  { path: "/settings", component: SettingsModuleHome },
  // HRMS (under Settings)
  { path: "/settings/hrms", component: HrmsDashboard },
  { path: "/settings/hrms/holidays", component: HrmsHolidays },
  { path: "/settings/hrms/leave", component: HrmsLeave },
  { path: "/settings/hrms/competency", component: HrmsCompetency },
  { path: "/settings/hrms/training", component: HrmsTraining },
  { path: "/settings/hrms/attendance", component: HrmsAttendance },
  { path: "/settings/hrms/calendar", component: HrmsGlobalCalendar },
  { path: "/settings/hrms/performance", component: HrmsPerformance },
  { path: "/settings/hrms/documents", component: HrmsDocuments },
  { path: "/settings/system-config", component: SystemConfig },
  { path: "/settings/basic-info", component: BasicInfo },
  {
    path: "/settings/tenant-rental-config",
    component: TenantRentalConfig,
  },

  // Site Types
  { path: "/settings/site-types", component: SiteTypeList },
  { path: "/settings/site-types/create", component: SiteTypeCreate },
  { path: "/settings/site-types/edit/:id", component: SiteTypeEdit },

  // Tenant Locations
  { path: "/tenant-locations", component: () => <Navigate to="/tenant-locations/list" /> },
  { path: "/tenant-locations/list", component: TenantLocationList },
  { path: "/tenant-locations/create", component: TenantLocationCreate },
  { path: "/tenant-locations/view/:id", component: TenantLocationView },
  { path: "/tenant-locations/edit/:id", component: TenantLocationEdit },

  // Asset Management
  { path: "/assets", component: () => <Navigate to="/assets/list" /> },
  { path: "/assets/list", component: AssetList },
  { path: "/assets/create", component: AssetCreate },
  { path: "/assets/view/:id", component: AssetView },
  { path: "/assets/edit/:id", component: AssetEdit },

  // Client Rental Config
  {
    path: "/clients/:clientId/rental-config",
    component: ClientRentalConfig,
  },
  {
    path: "/clients/rental-config",
    component: ClientRentalConfig,
  },

  // Product Management / Inventory
  { path: "/inventory", component: InventoryModuleHome },
  { path: "/products", component: () => <Navigate to="/products/list" /> },
  { path: "/products/list", component: ProductList },
  { path: "/products/create", component: ProductCreate },
  { path: "/products/view/:id", component: ProductView },
  { path: "/products/edit/:id", component: ProductEdit },

  // Stock Items Management
  { path: "/stock-items", component: () => <Navigate to="/stock-items/list" /> },
  { path: "/stock-items/list", component: StockItemList },
  { path: "/stock-items/create", component: StockItemCreate },
  { path: "/stock-items/view/:id", component: StockItemView },
  { path: "/stock-items/edit/:id", component: StockItemEdit },
  { path: "/stock-items/movement-history/:productId", component: StockMovementHistory },
  { path: "/inventory/dashboard", component: InventoryDashboard },

  // Purchase Orders
  { path: "/purchase-orders", component: () => <Navigate to="/purchase-orders/list" /> },
  { path: "/purchase-orders/list", component: PurchaseOrderList },
  { path: "/purchase-orders/create", component: PurchaseOrderCreate },
  { path: "/purchase-orders/view/:id", component: PurchaseOrderView },
  { path: "/purchase-orders/edit/:id", component: PurchaseOrderEdit },

  // Suppliers
  { path: "/suppliers", component: () => <Navigate to="/suppliers/list" /> },
  { path: "/suppliers/list", component: SupplierList },
  { path: "/suppliers/create", component: SupplierCreate },
  { path: "/suppliers/view/:id", component: SupplierView },
  { path: "/suppliers/edit/:id", component: SupplierEdit },

  // Tenant Labour Rates
  { path: "/tenant-labour-rates", component: () => <Navigate to="/tenant-labour-rates/list" /> },
  { path: "/tenant-labour-rates/list", component: TenantLabourRateList },
  { path: "/tenant-labour-rates/create", component: TenantLabourRateCreate },
  { path: "/tenant-labour-rates/edit/:id", component: TenantLabourRateEdit },

  // Tenant Cartage Rates
  { path: "/tenant-cartage-rates", component: () => <Navigate to="/tenant-cartage-rates/list" /> },
  { path: "/tenant-cartage-rates/list", component: TenantCartageRateList },
  { path: "/tenant-cartage-rates/create", component: TenantCartageRateCreate },
  { path: "/tenant-cartage-rates/edit/:id", component: TenantCartageRateEdit },

  // Client Labour Rates + Client Cartage Rates — relocated to the Client view's
  // "Rate Overrides" tab (see clients-view-page). No standalone routes.

  // Quote Management
  { path: "/quotes", component: QuotesModuleHome },
  { path: "/quotes/preview/:id", component: QuotePreview },
  { path: "/quotes/view/:id", component: QuoteView },
  { path: "/quotes/edit/:id", component: EditQuoteContainer },
  { path: "/quotes/create", component: CreateQuoteContainer },
  { path: "/quotes/list", component: QuoteList },
  { path: "/quotes/kanban", component: QuotesKanban },
  { path: "/quotes/report", component: QuotesReport },

  // this route should be at the end of all other routes
  // eslint-disable-next-line react/display-name
  {
    path: "/",
    exact: true,
    component: () => <Navigate to="/admin-dashboard" />,
  },
  { path: "*", component: () => <Navigate to="/admin-dashboard" /> },
];

const publicRoutes = [
  // Authentication Page
  { path: "/logout", component: Logout },
  { path: "/login", component: Login },
  { path: "/otp-verification", component: OtpVerification },
  { path: "/forgot-password", component: ForgetPasswordPage },

  // Field App Auth (mobile-optimized)
  { path: "/field/login", component: FieldLogin },
  { path: "/field/otp", component: FieldOtpVerification },

  { path: "/auth-404-basic", component: Basic404 },
  { path: "/auth-404-cover", component: Cover404 },
  { path: "/auth-404-alt", component: Alt404 },
  { path: "/auth-500", component: Error500 },
  { path: "/auth-offline", component: Offlinepage },
];

// ══════════════════════════════════════════════════════════════
// Field App Routes — Uses FieldAppShell (separate mobile layout)
// NOT rendered inside TopDockLayout or VerticalLayout.
// ══════════════════════════════════════════════════════════════
const fieldAppRoutes = [
  { path: "/field", component: FieldDashboard },
  // Enterprise IA tabs
  { path: "/field/tasks", component: FieldTasks },
  { path: "/field/analytics", component: FieldAnalytics },
  { path: "/field/profile", component: FieldProfile },
  { path: "/field/time-tracking", component: WorkerDayView },
  { path: "/field/activity/:id", component: ActivityDetail },
  // Scaffold view — shared by all personas (read in Phase 1)
  { path: "/field/scaffolds", component: FieldScaffolds },
  { path: "/field/scaffold/:id", component: FieldScaffold },
  // Supervisor (crew lead)
  { path: "/field/crew", component: FieldCrew },
  { path: "/field/capture/:id", component: FieldCapture },
  // Compliance inspector
  { path: "/field/visits", component: FieldVisits },
  { path: "/field/visit/:id", component: FieldVisitDetail },
  { path: "/field/checklists", component: FieldChecklists },
];

export { authProtectedRoutes, publicRoutes, fieldAppRoutes };
